import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { ACCESS_TOKEN, API_BASE_URL } from '../../assets/constants'
import {
	attempt401RefreshRetry,
	ensureValidAccessToken,
	scheduleProactiveAccessTokenRefresh
} from '../auth/tokenRefresh'
import ROUTES from '../../assets/routes'
import commonActions from '../../actions/common.actions'
import store from '../../store'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { isAccessTokenValid, isAuthenticated } from '../../util/authentication'
import { isEmbeddedModeSet } from '../../util/general'
import { Platform, platformToJSON, RequestHeader, requestHeaderToJSON } from '../../proto/generated/common'
import { applyClientVersionHeader, handleClientVersionError, handleClientVersionSuccessResponse} from '../../util/clientVersion'
import { currentAdapter } from './axiosClient'

const isUnloadError = (error: any): boolean => {
    return (
        error.code === 'ERR_CANCELED' ||
        error.message === 'canceled' ||
        error.name === 'AbortError' ||
        error.code === 'ERR_NETWORK' ||
        !error.response
    );
};

const apiV2 = axios.create({
    baseURL: API_BASE_URL
})

const requestPathsToBeControlled: string[] = ['/options', '/futures', '/v2/options']

interface QueuedRequest {
    resolve: (value?: AxiosResponse<any> | PromiseLike<AxiosResponse<any>>) => void;
    reject: (reason?: any) => void;
}

const inProgressRequests = new Map<string, QueuedRequest[]>()

const handleErrorResponse = async (error: any) => {
    if (isUnloadError(error)) {
        return Promise.reject(error);
    }

    if (!error.response) {
        if (!isUnloadError(error)) {
            commonActions.setApiError(store.dispatch, {
                error: true,
                message: 'Network error occurred',
                errorType: 'error'
            })
        }
        return Promise.reject(error);
    }

    const recovered = await attempt401RefreshRetry(apiV2, error)
    if (recovered) {
        return recovered
    }

    const textDecoder = new TextDecoder('utf-8')
    let errorMessage = 'Something went wrong'

    if (error.response.status === 401) {
        if (isEmbeddedModeSet()) {
            return Promise.reject(error)
        }
        currentAdapter.logout()
        AnalyticsManager.reset()
        if (window.location.pathname !== ROUTES.login) {
            window.open(window.origin + ROUTES.login, '_self')
        }
        errorMessage = error?.config?.responseType === 'json'
            ? typeof error?.response?.data === 'string'
                ? error?.response?.data
                : error?.response?.data?.message ?? ''
            : error?.response?.data ? textDecoder.decode(error.response.data) : 'Unauthorized'
    } else if (handleClientVersionError(error)) {
        return Promise.reject(error)
    } else if (error.response.status === 432) {
        window.open(window.origin + ROUTES.blocked, '_self')
    } else if (error.response.status === 433) {
        window.open(window.origin + ROUTES.waitlist, '_self')
    } else {
        errorMessage = error?.config?.responseType === 'json'
            ? typeof error?.response?.data === 'string'
                ? error?.response?.data
                : error?.response?.data?.message ?? ''
            : error?.response?.data ? textDecoder.decode(error.response.data) : errorMessage
    }

    commonActions.setApiError(store.dispatch, {
        error: true,
        message: errorMessage,
        errorType: 'error'
    })

    return Promise.reject(error)
}

apiV2.interceptors.request.use(
    async (config: any) => {
        let token = localStorage.getItem(ACCESS_TOKEN)
        const requestId = (config?.url ?? '') + config.method + JSON.stringify(config?.params ?? {}) + JSON.stringify(config?.data ?? {})

        if (token) {
            if (!isAccessTokenValid()) {
                if (isEmbeddedModeSet()) {
                    token = await ensureValidAccessToken()
                    if (!token) {
                        return Promise.reject(new Error('Waiting for host access token'))
                    }
                } else if (!isAuthenticated()) {
                    currentAdapter.logout()
                    window.open(window.origin + ROUTES.login, '_self')
                    return Promise.reject(new Error('Request stopped by interceptor'))
                }
            }
            config.headers['Authorization'] = 'Bearer ' + token
        }
        config.headers['Content-Type'] = 'application/json'
        config.headers[requestHeaderToJSON(RequestHeader.PLATFORM)] = platformToJSON(Platform.WEB)
        config.headers[requestHeaderToJSON(RequestHeader.ENVIRONMENT)] = import.meta.env.MODE
        applyClientVersionHeader(config)

        if (requestPathsToBeControlled.some(path => (config?.url ?? '').includes(path))) {
            if (inProgressRequests.has(requestId)) {
                return new Promise<AxiosRequestConfig>((resolve, reject) => {
                    inProgressRequests.get(requestId)?.push({ resolve: resolve as any, reject })
                })
            } else {
                (config as any).requestId = requestId
                inProgressRequests.set(requestId, [])
                return config
            }
        }

        return config
    },
    error => Promise.reject(error)
)

apiV2.interceptors.response.use(
    response => {
        const requestId = (response.config as any).requestId
        const queuedRequests = inProgressRequests.get(requestId)

        if (queuedRequests) {
            queuedRequests.forEach(({ resolve }) => resolve(response))
            inProgressRequests.delete(requestId)
        }

        handleClientVersionSuccessResponse(response)

        // Handle success if status is 200
        if (response.status === 200) {
            return response
        } else {
            // Trigger error handling for non-200 responses
            return Promise.reject(new Error('Non-200 response received'))
        }
    },
    error => {
        const requestId = (error?.config as any)?.requestId
        const queuedRequests = inProgressRequests.get(requestId)

        if (queuedRequests) {
            queuedRequests.forEach(({ reject }) => reject(error))
            inProgressRequests.delete(requestId)
        }

        return handleErrorResponse(error)
    }
)

scheduleProactiveAccessTokenRefresh()

export default apiV2
